/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, memo, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Col, Row, Space, Typography } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { GraphContext } from '../../FlowGraph';
import { GRAPH_LAYOUTS } from '../../../../constants/flowGraph';
import NodeWrapper from '../../NodeWrapper';
import LimitedConnectionsHandle from '../../LimitedConnectionsHandle';
import ResponsiveTagsList from '../../../UI/ResponsiveTagsList';

// Misc imports
import { NODE_TYPES_PROPERTIES } from '../../../../constants/flowGraph';
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';

// Constant declarations
const { Text } = Typography;

const AnalyticTagsNode = ({ id, type, data, dragging, selected }) => {
  const nodeProps = NODE_TYPES_PROPERTIES[type];
  const { t } = useTranslation();
  const { graphDirection } = useContext(GraphContext);
  const { currentBotLanguage } = useBotLanguage();

  const calculateMaxTagCount = (tagsCount, otherTagsCount) => {
    if (otherTagsCount > 0) {
      return 1; // If more than 3 tags, or tags exist in both fields, show 1 and "+N more"
    }
    return 3; // Otherwise, show up to 3 tags
  };

  const NodeContents = useMemo(() => {
    const addedTagsCount = calculateMaxTagCount(data.analyticTags?.length || 0, data.analyticTagsRemoved?.length || 0);
    const removedTagsCount = calculateMaxTagCount(data.analyticTagsRemoved?.length || 0, data.analyticTags?.length || 0);

    return (
      <Fragment>
        <LimitedConnectionsHandle type="target" position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'top' : 'left'}/>

        <Space size={6} className="node-header">
          {nodeProps.icon}
          {nodeProps.label}
        </Space>

        {
          data.analyticTags?.length > 0 ? (
            <Row>
              <Col span={24}>
                <Text italic className="fs-sm pb-1">
                  <Space size={4}>
                    <CheckOutlined className="text-success" />
                    {t('Tags added:')}
                  </Space>
                </Text>
              </Col>

              <Col span={24}>
                <ResponsiveTagsList
                  maxTagCount={addedTagsCount}
                  direction={'vertical'}
                  omittedButtonClassName="w-100"
                  values={data.analyticTags.map((tag) => ({
                    label: tag,
                    value: tag
                  }))}
                  tagRender={(tag) =>
                    <span style={{ width: 120 }} className="ant-select-selection-item ant-select-selection-item-content">
                      {tag.label}
                    </span>
                  }
                />
              </Col>
            </Row>
          ) : null
        }

        {
          data.analyticTagsRemoved?.length > 0 ? (
            <Row>
              <Col span={24}>
                <Text italic className="fs-sm pb-1">
                  <Space size={4}>
                    <CloseOutlined className="text-error" />
                    {t('Tags removed:')}
                  </Space>
                </Text>
              </Col>

              <Col span={24}>
                <ResponsiveTagsList
                  maxTagCount={removedTagsCount}
                  direction={'vertical'}
                  omittedButtonClassName="w-100"
                  values={data.analyticTagsRemoved.map((tag) => ({
                    label: tag,
                    value: tag
                  }))}
                  tagRender={(tag) => <span style={{ width: 120 }} className="ant-select-selection-item ant-select-selection-item-content">{tag.label}</span>}
                />
              </Col>
            </Row>
          ) : null
        }

        <Text type="secondary">
          {t('Click to edit tags')}
        </Text>

        <LimitedConnectionsHandle type="source" position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'bottom' : 'right'}/>
      </Fragment>
    );
  }, [data, nodeProps, graphDirection, currentBotLanguage]);

  return (
    <NodeWrapper
      id={id}
      type={type}
      data={data}
      children={NodeContents}
      dragging={dragging}
      selected={selected}
    />
  );
};

// https://reactflow.dev/docs/api/nodes/node-options/
AnalyticTagsNode.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string,
  dragging: PropTypes.bool,
  selected: PropTypes.bool,
  type: PropTypes.string,
  xPos: PropTypes.number,
  yPos: PropTypes.number
};

export default memo(AnalyticTagsNode);
